import React, { useState, useEffect } from 'react';
import { Shield, X, Heart, Coffee } from 'lucide-react';
import { isAdBlockerActive, checkAdSenseScriptLoad, isFirefoxETPEnabled } from './AdUtils';

interface AdBlockerNoticeProps {
  className?: string;
  onDismiss?: () => void;
}

export const AdBlockerNotice: React.FC<AdBlockerNoticeProps> = ({ 
  className = '',
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAdBlocker = async () => {
      try {
        // Check if AdSense script can load
        const scriptLoads = await checkAdSenseScriptLoad();
        const adBlockerActive = await isAdBlockerActive();
        const isFirefox = isFirefoxETPEnabled();
        
        // Show notice if ad blocker is detected or Firefox ETP is blocking ads
        if (!scriptLoads || adBlockerActive || isFirefox) {
          const dismissed = localStorage.getItem('adblocker-notice-dismissed') === 'true';
          if (!dismissed) {
            setIsVisible(true);
          }
        }
      } catch (error) {
        console.log('Ad blocker check failed:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkAdBlocker();
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem('adblocker-notice-dismissed', 'true');
    onDismiss?.();
  };

  if (isChecking || !isVisible || isDismissed) {
    return null;
  }

  return (
    <div className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[80] bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg shadow-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">
            {isFirefoxETPEnabled() ? 'Firefox Protection Active' : 'Ad Blocker Detected'}
          </h4>
          <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
            {isFirefoxETPEnabled() 
              ? 'Firefox Enhanced Tracking Protection is blocking ads. Click the shield icon in your address bar to disable it for this site.'
              : 'We noticed you\'re using an ad blocker. Our free service is supported by ads. Consider whitelisting our site to support development.'
            }
          </p>
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-3 h-3 text-red-500" />
            <span className="text-xs text-blue-600 dark:text-blue-400">
              Every ad view helps us keep this service free
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDismiss}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline"
            >
              I understand
            </button>
            <button
              onClick={handleDismiss}
              className="text-xs text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-200"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200 transition-colors"
          aria-label="Close notice"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AdBlockerNotice;
